import React, { useState, useRef, DragEvent, useEffect } from 'react';
import { X, Upload, FileText, GitBranch, GitCommit, Zap, Package, AlertCircle } from 'lucide-react';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import { CreateReleaseInput } from '../../api/hooks/updates/useReleaseMutation';
import { useBinaryUpload, useChunkedUpload } from '../../api/hooks/binaries';
import { useReleases } from '../../api/hooks/updates';
import { getNextVersion, VersionBump } from '../../utils/semver';

interface ReleaseUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateReleaseInput) => Promise<void>;
  isSubmitting: boolean;
}

export const ReleaseUploadModal: React.FC<ReleaseUploadModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState<CreateReleaseInput>({
    version: '',
    channel: 'stable',
    platform: 'win32',
    architecture: 'x64',
    fileUrl: '',
    fileSize: 0,
    sha512: '',
    releaseNotes: '',
    minimumOsVersion: '',
    isMandatory: false,
    isDraft: true, // Default to draft
    rolloutPercentage: 100,
  });
  
  const [versionMode, setVersionMode] = useState<'manual' | 'auto'>('auto');
  const [versionBump, setVersionBump] = useState<VersionBump>('patch');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadMode, setUploadMode] = useState<'file' | 'url'>('file');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [isDraggingMain, setIsDraggingMain] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { data: releases = [] } = useReleases();

  const binaryUpload = useBinaryUpload((progress) => {
    setUploadProgress(progress.percentage);
  });

  const chunkedUpload = useChunkedUpload({
    onProgress: (progress) => {
      setUploadProgress(progress.percentage);
      setUploadStatus(`Uploading chunks: ${progress.currentChunk}/${progress.totalChunks} completed`);
    }
  });

  
  // Update version when mode, channel, platform, or bump type changes
  useEffect(() => {
    if (versionMode === 'auto') {
      // Filter releases by platform to allow different versions for Windows and Mac
      const platformReleases = releases.filter(r => r.platform === formData.platform);
      const nextVersion = getNextVersion(platformReleases, formData.channel, versionBump);
      setFormData(prev => ({ ...prev, version: nextVersion }));
    }
  }, [versionMode, formData.channel, formData.platform, versionBump, releases]);


  const detectPlatform = (fileName: string): 'win32' | 'darwin' => {
    const name = fileName.toLowerCase();
    if (name.endsWith('.exe') || name.endsWith('.nupkg')) return 'win32';
    if (name.endsWith('.dmg') || name.endsWith('.zip')) return 'darwin';
    return 'win32'; // Default to Windows
  };

  const detectArchitecture = (fileName: string): 'x64' | 'arm64' | 'ia32' => {
    const name = fileName.toLowerCase();
    if (name.includes('arm64') || name.includes('aarch64')) return 'arm64';
    if (name.includes('ia32') || name.includes('i386') || name.includes('x86') && !name.includes('x86_64')) return 'ia32';
    return 'x64';
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-detect platform and architecture from filename
      const platform = detectPlatform(file.name);
      const architecture = detectArchitecture(file.name);
      setFormData(prev => ({ ...prev, platform, architecture }));
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingMain(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingMain(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingMain(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      // Auto-detect platform and architecture from filename
      const platform = detectPlatform(file.name);
      const architecture = detectArchitecture(file.name);
      setFormData(prev => ({ ...prev, platform, architecture }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    const newErrors: Record<string, string> = {};
    if (!formData.version) newErrors.version = 'Version is required';

    if (uploadMode === 'file') {
      if (!selectedFile) newErrors.file = 'File is required';
    } else {
      if (!formData.fileUrl) newErrors.fileUrl = 'File URL is required';
      if (!formData.fileSize || formData.fileSize <= 0) newErrors.fileSize = 'Valid file size is required';
      if (!formData.sha512) newErrors.sha512 = 'SHA512 hash is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      let releaseData = { ...formData };

      if (uploadMode === 'file' && selectedFile) {
        // Upload the main file
        setUploadProgress(0);
        setUploadStatus('');
        
        // Use chunked upload for files larger than 20MB
        const USE_CHUNKED_THRESHOLD = 20 * 1024 * 1024; // 20MB
        
        let uploadResponse;
        
        if (selectedFile.size > USE_CHUNKED_THRESHOLD) {
          // Use chunked upload for large files
          setUploadStatus('Preparing upload...');
          uploadResponse = await chunkedUpload.upload(selectedFile, {
            version: formData.version,
            channel: formData.channel,
            platform: formData.platform,
            architecture: formData.architecture,
            releaseNotes: formData.releaseNotes,
            minimumAppVersion: formData.minimumAppVersion,
            isMandatory: formData.isMandatory,
          });
        } else {
          // Use regular upload for small files
          uploadResponse = await binaryUpload.mutateAsync({
            file: selectedFile,
            metadata: {
              version: formData.version,
              channel: formData.channel,
              platform: formData.platform,
              architecture: formData.architecture,
              releaseNotes: formData.releaseNotes,
              minimumAppVersion: formData.minimumAppVersion,
              isMandatory: formData.isMandatory,
            }
          });
        }
        
        // The upload response already contains the created release
        if (uploadResponse.release) {
          // Release was created during upload, just notify success
          await onSubmit(uploadResponse.release);
          return;
        }
        
        // Fallback: if no release in response, create one (shouldn't happen)
        releaseData.fileUrl = uploadResponse.fileUrl;
        releaseData.fileSize = uploadResponse.fileSize;
        releaseData.sha512 = uploadResponse.sha512;
      }

      await onSubmit(releaseData);
      
      // Reset form on success
      setFormData({
        version: '',
        channel: 'stable',
        platform: 'win32',
        architecture: 'x64',
        fileUrl: '',
        fileSize: 0,
        sha512: '',
        releaseNotes: '',
        minimumOsVersion: '',
        isMandatory: false,
        isDraft: true,
        rolloutPercentage: 100,
      });
      setSelectedFile(null);
      setUploadProgress(0);
    } catch (error) {
      console.error('Error uploading release:', error);
      setErrors({ submit: error instanceof Error ? error.message : 'Upload failed' });
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  if (!isOpen) return null;

  const isProcessing = binaryUpload.isPending || chunkedUpload.isUploading || isSubmitting;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Upload New Release</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={isProcessing}>
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Upload Mode Selector */}
          <div className="flex space-x-4 mb-4">
            <button
              type="button"
              onClick={() => setUploadMode('file')}
              className={`px-4 py-2 rounded-md ${
                uploadMode === 'file'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              disabled={isProcessing}
            >
              Upload File
            </button>
            <button
              type="button"
              onClick={() => setUploadMode('url')}
              className={`px-4 py-2 rounded-md ${
                uploadMode === 'url'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              disabled={isProcessing}
            >
              Enter URL
            </button>
          </div>

          {/* Version Selection */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Version Strategy</label>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setVersionMode('auto')}
                  className={`flex-1 px-4 py-2 rounded-md border-2 transition-colors ${
                    versionMode === 'auto'
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  disabled={isProcessing}
                >
                  <div className="font-medium">Automatic Versioning</div>
                  <div className="text-xs mt-1">Calculate next version</div>
                </button>
                <button
                  type="button"
                  onClick={() => setVersionMode('manual')}
                  className={`flex-1 px-4 py-2 rounded-md border-2 transition-colors ${
                    versionMode === 'manual'
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  disabled={isProcessing}
                >
                  <div className="font-medium">Manual Version</div>
                  <div className="text-xs mt-1">Enter custom version</div>
                </button>
              </div>
            </div>

            {versionMode === 'auto' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Version Bump Type</label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setVersionBump('patch')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      versionBump === 'patch'
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    disabled={isProcessing}
                  >
                    <Zap className="h-5 w-5 mx-auto mb-1" />
                    <div className="font-medium">Patch</div>
                    <div className="text-xs text-gray-600 mt-1">Bug fixes</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setVersionBump('minor')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      versionBump === 'minor'
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    disabled={isProcessing}
                  >
                    <GitCommit className="h-5 w-5 mx-auto mb-1" />
                    <div className="font-medium">Minor</div>
                    <div className="text-xs text-gray-600 mt-1">New features</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setVersionBump('major')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      versionBump === 'major'
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    disabled={isProcessing}
                  >
                    <GitBranch className="h-5 w-5 mx-auto mb-1" />
                    <div className="font-medium">Major</div>
                    <div className="text-xs text-gray-600 mt-1">Breaking changes</div>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Version</label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="1.0.0"
                  className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm ${
                    versionMode === 'auto' 
                      ? 'bg-gray-50 text-gray-600 cursor-not-allowed' 
                      : 'focus:border-indigo-500 focus:ring-indigo-500'
                  }`}
                  disabled={isProcessing || versionMode === 'auto'}
                  readOnly={versionMode === 'auto'}
                />
                {versionMode === 'auto' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Auto-calculated based on {formData.platform === 'darwin' ? 'macOS' : 'Windows'} {formData.channel} releases
                  </p>
                )}
              </div>
              {errors.version && <p className="mt-1 text-sm text-red-600">{errors.version}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Channel</label>
              <select
                value={formData.channel}
                onChange={(e) => setFormData({ ...formData, channel: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                disabled={isProcessing}
              >
                <option value="stable">Stable</option>
                <option value="beta">Beta</option>
                <option value="alpha">Alpha</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Platform</label>
              <select
                value={formData.platform}
                onChange={(e) => setFormData({ ...formData, platform: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                disabled={isProcessing}
              >
                <option value="win32">Windows</option>
                <option value="darwin">macOS</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Architecture</label>
              <select
                value={formData.architecture}
                onChange={(e) => setFormData({ ...formData, architecture: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                disabled={isProcessing}
              >
                <option value="x64">x64</option>
                <option value="arm64">ARM64</option>
                <option value="ia32">x86</option>
              </select>
            </div>
          </div>

          {uploadMode === 'file' ? (
            <>
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Release File</label>
                <div 
                  className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                    isDraggingMain 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => !isProcessing && fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {selectedFile ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <div className="text-left">
                          <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                        </div>
                      </div>
                      {!isProcessing && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(null);
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <Upload className={`mx-auto h-8 w-8 ${isDraggingMain ? 'text-indigo-500' : 'text-gray-400'}`} />
                      <p className="mt-2 text-sm text-gray-600">
                        {isDraggingMain ? 'Drop file here' : 'Click to select or drag and drop file'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Supported: .exe, .dmg, .zip
                      </p>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".exe,.dmg,.zip,.nupkg"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={isProcessing}
                  />
                </div>
                {errors.file && <p className="mt-1 text-sm text-red-600">{errors.file}</p>}
              </div>

              {/* Upload Progress */}
              {binaryUpload.isPending && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Manual URL Entry */}
              <div>
                <label className="block text-sm font-medium text-gray-700">File URL</label>
                <input
                  type="url"
                  value={formData.fileUrl}
                  onChange={(e) => setFormData({ ...formData, fileUrl: e.target.value })}
                  placeholder="https://example.com/releases/app-1.0.0.exe"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  disabled={isProcessing}
                />
                {errors.fileUrl && <p className="mt-1 text-sm text-red-600">{errors.fileUrl}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">File Size (bytes)</label>
                  <input
                    type="number"
                    value={formData.fileSize || ''}
                    onChange={(e) => setFormData({ ...formData, fileSize: parseInt(e.target.value) || 0 })}
                    placeholder="52428800"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    disabled={isProcessing}
                  />
                  {errors.fileSize && <p className="mt-1 text-sm text-red-600">{errors.fileSize}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Minimum App Version</label>
                  <input
                    type="text"
                    value={formData.minimumAppVersion || ''}
                    onChange={(e) => setFormData({ ...formData, minimumAppVersion: e.target.value })}
                    placeholder="0.9.0"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    disabled={isProcessing}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">SHA512 Hash</label>
                <input
                  type="text"
                  value={formData.sha512}
                  onChange={(e) => setFormData({ ...formData, sha512: e.target.value })}
                  placeholder="abc123..."
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 font-mono text-sm"
                  disabled={isProcessing}
                />
                {errors.sha512 && <p className="mt-1 text-sm text-red-600">{errors.sha512}</p>}
              </div>

            </>
          )}

          {/* Common Fields */}
          {uploadMode === 'url' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Minimum App Version</label>
              <input
                type="text"
                value={formData.minimumAppVersion || ''}
                onChange={(e) => setFormData({ ...formData, minimumAppVersion: e.target.value })}
                placeholder="0.9.0"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                disabled={isProcessing}
              />
            </div>
          )}

          {/* Minimum OS Version */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Minimum OS Version</label>
            <input
              type="text"
              value={formData.minimumOsVersion || ''}
              onChange={(e) => setFormData({ ...formData, minimumOsVersion: e.target.value })}
              placeholder={formData.platform === 'darwin' ? '10.15 (Catalina)' : '10 (Windows 10)'}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              disabled={isProcessing}
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.platform === 'darwin'
                ? 'e.g., 10.15 for macOS Catalina, 11.0 for Big Sur'
                : 'e.g., 10 for Windows 10, 11 for Windows 11'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Release Notes</label>
            <textarea
              value={formData.releaseNotes || ''}
              onChange={(e) => setFormData({ ...formData, releaseNotes: e.target.value })}
              rows={3}
              placeholder="Bug fixes and improvements..."
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              disabled={isProcessing}
            />
          </div>

          {/* Release Status Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Release Status</h3>
            <div className="space-y-3">
              {/* Draft */}
              <label className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-100 cursor-pointer">
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-gray-500" />
                  <div>
                    <span className="font-medium">Draft</span>
                    <p className="text-sm text-gray-500">Release is not yet published</p>
                  </div>
                </div>
                <input
                  type="radio"
                  name="releaseStatus"
                  checked={formData.isDraft}
                  onChange={() => setFormData({
                    ...formData,
                    isDraft: true,
                    isMandatory: false
                  })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  disabled={isProcessing}
                />
              </label>

              {/* Published - Optional */}
              <label className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-100 cursor-pointer">
                <div className="flex items-center gap-3">
                  <Upload className="h-5 w-5 text-gray-500" />
                  <div>
                    <span className="font-medium">Published - Optional Update</span>
                    <p className="text-sm text-gray-500">Users can choose when to update</p>
                  </div>
                </div>
                <input
                  type="radio"
                  name="releaseStatus"
                  checked={!formData.isDraft && !formData.isMandatory}
                  onChange={() => setFormData({
                    ...formData,
                    isDraft: false,
                    isMandatory: false
                  })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  disabled={isProcessing}
                />
              </label>

              {/* Published - Mandatory */}
              <label className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-100 cursor-pointer">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-gray-500" />
                  <div>
                    <span className="font-medium">Published - Mandatory Update</span>
                    <p className="text-sm text-gray-500">Users must update to continue using the app</p>
                  </div>
                </div>
                <input
                  type="radio"
                  name="releaseStatus"
                  checked={!formData.isDraft && formData.isMandatory}
                  onChange={() => setFormData({
                    ...formData,
                    isDraft: false,
                    isMandatory: true
                  })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  disabled={isProcessing}
                />
              </label>
            </div>
          </div>

          {/* Rollout Configuration - Hidden (functionality preserved in backend) */}
          {/* Uncomment to re-enable rollout percentage UI */}
          {/*
          <div className="border-t pt-4">
            <button
              type="button"
              onClick={() => setShowRolloutConfig(!showRolloutConfig)}
              className="flex items-center justify-between w-full text-left"
              disabled={isProcessing}
            >
              <h3 className="text-lg font-medium text-gray-900">Rollout Configuration</h3>
              {showRolloutConfig ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>

            {showRolloutConfig && (
              <div className="mt-4 space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rollout Percentage
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={formData.rolloutPercentage}
                        onChange={(e) => setFormData({ ...formData, rolloutPercentage: parseInt(e.target.value) })}
                        className="flex-1"
                        disabled={isProcessing}
                      />
                      <div className="w-20">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.rolloutPercentage}
                          onChange={(e) => setFormData({ ...formData, rolloutPercentage: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          disabled={isProcessing}
                        />
                      </div>
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      {formData.rolloutPercentage === 100
                        ? 'All users will receive this update'
                        : `Approximately ${formData.rolloutPercentage}% of users will receive this update`}
                    </p>
                  </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <h5 className="text-sm font-medium text-blue-900 mb-1">Rollout Summary</h5>
                  <p className="text-sm text-blue-700">
                    This release will be available to {formData.rolloutPercentage}% of all users
                  </p>
                </div>
              </div>
            )}

            {showRolloutConfig && formData.isDraft && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Rollout configuration is disabled for draft releases. You can configure rollout settings after publishing the release.
                </p>
              </div>
            )}
          </div>
          */}

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{errors.submit}</p>
            </div>
          )}

          {/* Upload progress */}
          {isProcessing && uploadProgress > 0 && (
            <div className="mt-4 space-y-2">
              <div className="bg-gray-200 rounded-full h-3 relative overflow-hidden">
                <div
                  className="bg-indigo-600 h-3 rounded-full transition-all duration-300 relative"
                  style={{ width: `${uploadProgress}%` }}
                >
                  {/* Animated stripes for active upload */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
                </div>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600 font-medium">{uploadProgress}% uploaded</p>
                {uploadStatus && (
                  <p className="text-sm text-gray-500">{uploadStatus}</p>
                )}
              </div>
              {chunkedUpload.isUploading && uploadStatus?.includes('chunks') && (
                <p className="text-xs text-gray-500 text-center">
                  🚀 Uploading 6 chunks in parallel for faster speed
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={onClose} disabled={isProcessing}>
              Cancel
            </Button>
            <Button type="submit" disabled={isProcessing}>
              {isProcessing ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner />
                  <span>
                    {uploadStatus || 
                     (binaryUpload.isPending ? 'Uploading...' : 
                      chunkedUpload.isUploading ? 'Uploading...' : 
                      'Creating Release...')}
                  </span>
                </div>
              ) : (
                formData.isDraft ? 'Save as Draft' : 'Upload Release'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};